import theme from '../../ordersTheme.module.scss';

/**
 * The shared chrome for every orders modal.
 *
 * `DBModal` has no `title` prop, so each of the fifteen modals on this screen
 * invented its own heading — 32px uppercase in one, 24px centred in the next,
 * a bare <h2> in a third — and its own idea of padding and footer alignment.
 * This is that missing convention: title, optional subtitle, optional back
 * arrow, a body that scrolls on its own, and a footer whose actions always sit
 * in the same place.
 *
 * The close affordance is deliberately absent: antd's Modal already renders
 * one, and a second button in the header was two ways to do the same thing.
 */
export const ModalShell = ({
  title,
  subtitle,
  onBack,
  backLabel = 'Back',
  footer,
  children,
  bodyClassName = '',
}) => (
  <div className={theme.shell}>
    <header className={theme.shellHead}>
      {onBack && (
        <button type="button" className={theme.shellBack} onClick={onBack}>
          ← {backLabel}
        </button>
      )}
      <h2 className={theme.shellTitle}>{title}</h2>
      {subtitle && <p className={theme.shellSubtitle}>{subtitle}</p>}
    </header>
    <div className={`${theme.shellBody} ${bodyClassName}`}>{children}</div>
    {footer && <footer className={theme.shellFoot}>{footer}</footer>}
  </div>
);

/**
 * A modal whose whole job is picking one of several routes.
 *
 * Three of these existed as near-identical files, each a column of six
 * identical 50px buttons where nothing but the first was distinguishable.
 */
export const ChoiceList = ({children}) => (
  <div className={theme.choiceList}>{children}</div>
);

export const ChoiceRow = ({label, description, onClick, disabled, hint}) => (
  <button
    type="button"
    className={theme.choiceRow}
    disabled={disabled}
    onClick={onClick}>
    <span className={theme.choiceText}>
      <span className={theme.choiceLabel}>{label}</span>
      {description && (
        <span className={theme.choiceDescription}>{description}</span>
      )}
    </span>
    {hint ? (
      <span className={theme.choiceHint}>{hint}</span>
    ) : (
      <span className={theme.choiceChevron}>›</span>
    )}
  </button>
);

/** A labelled group of fields inside a modal body. */
export const FieldGroup = ({title, description, children, columns = 2}) => (
  <section className={theme.fieldGroup}>
    {title && <h3 className={theme.fieldGroupTitle}>{title}</h3>}
    {description && <p className={theme.fieldGroupNote}>{description}</p>}
    <div className={columns === 1 ? theme.fieldStack : theme.fieldGrid}>
      {children}
    </div>
  </section>
);

/** Read-back rows for a confirmation step. */
export const SummaryRow = ({label, value}) => (
  <div className={theme.summaryRow}>
    <span className={theme.summaryLabel}>{label}</span>
    <span className={theme.summaryValue}>{value}</span>
  </div>
);

export const SummaryCard = ({children}) => (
  <div className={theme.summaryCard}>{children}</div>
);

/** Spells out a consequence at the point of committing to it. */
export const DangerNote = ({children}) => (
  <p className={theme.dangerNote}>{children}</p>
);
